import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, CheckCircle, AlertCircle, Loader2, LogIn } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinCareCircle() {
  const { user, token, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = (params.get('code') || '').trim().toUpperCase();
  const invitedEmail = params.get('email') || '';

  const [state, setState] = useState('idle'); // idle | joining | success | error
  const [message, setMessage] = useState('');

  const joinUrl = `/care-circle/join?code=${encodeURIComponent(code)}${invitedEmail ? `&email=${encodeURIComponent(invitedEmail)}` : ''}`;

  useEffect(() => {
    if (authLoading) return;
    if (!code) {
      setState('error');
      setMessage('This invite link is missing its code. Please use the link from your email.');
      return;
    }
    if (!user || !token) return; // show sign-in CTA below
    const join = async () => {
      setState('joining');
      try {
        await axios.post(`${API}/family/join?invite_code=${encodeURIComponent(code)}`, {}, getAuthHeader());
        setState('success');
        setMessage("You're now part of the care circle.");
      } catch (err) {
        setState('error');
        setMessage(err?.response?.data?.detail || 'We could not accept this invite. It may have already been used or expired.');
      }
    };
    join();
  }, [authLoading, code, user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-cn-bg flex flex-col items-center justify-center px-4 py-12" data-testid="join-care-circle-page">
      <Link to="/" className="mb-8 inline-flex" data-testid="join-home-link">
        <CaraLoomLogo size={34} />
      </Link>

      <Card className="w-full max-w-md border border-cn-border shadow-lg bg-white dark:bg-cn-ink-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-cn-text">
            <Heart className="w-5 h-5 text-cn-terracotta-500" />
            Join a care circle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {authLoading && (
            <div className="flex items-center gap-2 text-cn-text-muted" data-testid="join-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Checking your invite…
            </div>
          )}

          {!authLoading && !user && code && (
            <div className="space-y-4" data-testid="join-signin-cta">
              <p className="text-sm text-cn-text-muted">
                You've been invited to a CaraLoom care circle. Sign in or create a free account
                {invitedEmail ? <> using <span className="font-semibold text-cn-text">{invitedEmail}</span></> : ''} to accept.
              </p>
              <div className="rounded-xl border border-cn-border bg-cn-bg/50 p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-cn-text-muted">Your invite code</p>
                <p className="text-2xl font-bold tracking-widest text-cn-text mt-1" data-testid="join-invite-code">{code}</p>
              </div>
              <Button
                className="w-full cn-btn cn-btn-primary h-12"
                onClick={() => navigate(`/auth?next=${encodeURIComponent(joinUrl)}${invitedEmail ? `&email=${encodeURIComponent(invitedEmail)}` : ''}`)}
                data-testid="join-signin-btn"
              >
                <LogIn className="w-4 h-4 mr-2" /> Sign in to accept
              </Button>
            </div>
          )}

          {!authLoading && user && state === 'joining' && (
            <div className="flex items-center gap-2 text-cn-text-muted" data-testid="join-accepting">
              <Loader2 className="w-5 h-5 animate-spin" /> Accepting your invite…
            </div>
          )}

          {state === 'success' && (
            <div className="space-y-4 text-center" data-testid="join-success">
              <CheckCircle className="w-12 h-12 text-cn-sage-600 mx-auto" />
              <p className="text-cn-text font-medium">{message}</p>
              <Button className="w-full cn-btn cn-btn-primary h-11" onClick={() => navigate('/patient/dashboard')} data-testid="join-continue-btn">
                Continue
              </Button>
            </div>
          )}

          {state === 'error' && (
            <div className="space-y-4 text-center" data-testid="join-error">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
              <p className="text-cn-text">{message}</p>
              <Button variant="outline" className="w-full h-11" onClick={() => navigate('/')} data-testid="join-home-btn">
                Back to home
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
